import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import Navbar from "../home/Navbar/Navbar";

/* =========================================================
   API
========================================================= */

const SERVER_BASE_URL =
  "https://admin.monsinidress.com";

const SIZE_CHART_API_URL =
  `${SERVER_BASE_URL}/api/sizeChart`;

/* =========================================================
   BRAND
========================================================= */

const BRAND_NAME = "Azure";
const BRAND_KEY = "azure";

/* =========================================================
   PAGE DATA
========================================================= */

const SIZE_CHARTS = {
  prom: {
    title: "Azure Prom Size Chart",
    shortTitle: "Prom",
    description:
      "Find your perfect fit with the official Azure Prom size chart.",
  },

  evening: {
    title: "Azure Evening Size Chart",
    shortTitle: "Evening",
    description:
      "Use the official Azure Evening size guide to find your perfect fit.",
  },

  bridal: {
    title: "Azure Bridal Size Chart",
    shortTitle: "Bridal",
    description:
      "Find the ideal bridal fit using the official Azure Bridal size guide.",
  },
};

/* =========================================================
   NORMALIZE CATEGORY
========================================================= */

function normalizeCategory(categoryName = "") {
  const value = String(categoryName)
    .trim()
    .toLowerCase();

  /* Optional support:
     /size-chart/gowns -> prom
  */

  if (value === "gowns") {
    return "prom";
  }

  if (value.includes("prom")) {
    return "prom";
  }

  if (value.includes("evening")) {
    return "evening";
  }

  if (value.includes("bridal")) {
    return "bridal";
  }

  return value;
}

/* =========================================================
   NORMALIZE BRAND
========================================================= */

function normalizeBrand(brandName = "") {
  return String(brandName)
    .trim()
    .toLowerCase();
}

/* =========================================================
   IMAGE URL
========================================================= */

function resolveImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value =
    String(imagePath).trim();

  if (
    /^(https?:\/\/|data:|blob:)/i.test(
      value
    )
  ) {
    return value;
  }

  const cleanPath =
    value.replace(/^\/+/, "");

  return `${SERVER_BASE_URL}/${cleanPath}`;
}

/* =========================================================
   GRID
========================================================= */

function getGridClass(totalCards) {
  if (totalCards === 1) {
    return "grid grid-cols-1 gap-7";
  }

  if (totalCards === 2) {
    return "grid grid-cols-1 gap-7 md:grid-cols-2";
  }

  if (totalCards === 3) {
    return "grid grid-cols-1 gap-7 sm:grid-cols-2 xl:grid-cols-3";
  }

  return "grid grid-cols-1 gap-7 sm:grid-cols-2 xl:grid-cols-4";
}

/* =========================================================
   COMPONENT
========================================================= */

export default function SizeChart() {
  const { category } =
    useParams();

  /* =======================================================
     CATEGORY
  ======================================================= */

  const categoryKey =
    useMemo(() => {
      return normalizeCategory(
        category
      );
    }, [category]);

  /* =======================================================
     PAGE DATA
  ======================================================= */

  const chart =
    useMemo(() => {
      return (
        SIZE_CHARTS[
          categoryKey
        ] || null
      );
    }, [categoryKey]);

  /* =======================================================
     STATE
  ======================================================= */

  const [
    imageSources,
    setImageSources,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    retryCount,
    setRetryCount,
  ] = useState(0);

  /* =======================================================
     FETCH SIZE CHART
  ======================================================= */

  const fetchSizeCharts =
    useCallback(
      async (signal) => {
        if (!chart) {
          setLoading(false);
          setImageSources([]);
          setError("");

          return;
        }

        setLoading(true);
        setError("");
        setImageSources([]);

        try {
          const response =
            await fetch(
              SIZE_CHART_API_URL,
              {
                method: "GET",

                headers: {
                  Accept:
                    "application/json",
                },

                signal,

                cache:
                  "no-store",
              }
            );

          /* =============================================
             API ERROR
          ============================================= */

          if (!response.ok) {
            throw new Error(
              `Unable to load ${chart.title}. Server returned ${response.status}.`
            );
          }

          const result =
            await response.json();

          console.log(
            "=================================="
          );

          console.log(
            "ALL SIZE CHART DATA:",
            result
          );

          console.log(
            "CURRENT BRAND:",
            BRAND_NAME
          );

          console.log(
            "URL CATEGORY:",
            category
          );

          console.log(
            "NORMALIZED CATEGORY:",
            categoryKey
          );

          /* =============================================
             API RESULT ARRAY
          ============================================= */

          const apiData =
            Array.isArray(result)
              ? result
              : Array.isArray(
                    result?.data
                  )
                ? result.data
                : [];

          console.log(
            "SIZE CHART API DATA:",
            apiData
          );

          /* =============================================
             FILTER

             BRAND:
             Azure

             CATEGORY:
             Prom / Evening / Bridal
          ============================================= */

          const filteredData =
            apiData

              .filter(
                (item) => {
                  const itemCategory =
                    normalizeCategory(
                      item?.category ||
                        ""
                    );

                  const itemBrand =
                    normalizeBrand(
                      item?.brand ||
                        ""
                    );

                  const categoryMatches =
                    itemCategory ===
                    categoryKey;

                  const brandMatches =
                    itemBrand ===
                    BRAND_KEY;

                  console.log(
                    "SIZE CHART ITEM:",
                    {
                      brand:
                        item?.brand,

                      normalizedBrand:
                        itemBrand,

                      category:
                        item?.category,

                      normalizedCategory:
                        itemCategory,

                      year:
                        item?.year,

                      categoryMatches,

                      brandMatches,
                    }
                  );

                  return (
                    categoryMatches &&
                    brandMatches
                  );
                }
              )

              /* =========================================
                 NEWEST YEAR FIRST

                 2027
                 2026
                 2025
              ========================================= */

              .sort(
                (a, b) => {
                  const yearA =
                    Number(
                      a?.year ||
                        0
                    );

                  const yearB =
                    Number(
                      b?.year ||
                        0
                    );

                  return (
                    yearB -
                    yearA
                  );
                }
              );

          console.log(
            "FILTERED AZURE SIZE CHARTS:",
            filteredData
          );

          /* =============================================
             NOTHING FOUND
          ============================================= */

          if (
            !filteredData.length
          ) {
            throw new Error(
              `No Azure ${chart.shortTitle} size charts are currently available.`
            );
          }

          /* =============================================
             FINAL DATA
          ============================================= */

          const finalCharts =
            filteredData

              .map(
                (
                  item,
                  index
                ) => {
                  const imagePath =
                    item?.image ||
                    item?.imageUrl ||
                    item?.imageURL ||
                    item?.chartImage ||
                    "";

                  const image =
                    resolveImageUrl(
                      imagePath
                    );

                  return {
                    id:
                      item?._id ||
                      item?.id ||
                      `${categoryKey}-${item?.year}-${index}`,

                    year:
                      item?.year ||
                      "",

                    category:
                      item?.category ||
                      chart.shortTitle,

                    brand:
                      item?.brand ||
                      BRAND_NAME,

                    image,
                  };
                }
              )

              .filter(
                (item) =>
                  Boolean(
                    item.image
                  )
              );

          console.log(
            "FINAL AZURE SIZE CHARTS:",
            finalCharts
          );

          console.log(
            "=================================="
          );

          if (
            !finalCharts.length
          ) {
            throw new Error(
              `${chart.title} images were not found.`
            );
          }

          setImageSources(
            finalCharts
          );
        } catch (
          requestError
        ) {
          if (
            requestError.name ===
            "AbortError"
          ) {
            return;
          }

          console.error(
            "AZURE SIZE CHART ERROR:",
            requestError
          );

          setImageSources(
            []
          );

          setError(
            requestError.message ||
              "The size chart could not be loaded. Please try again."
          );
        } finally {
          if (
            !signal.aborted
          ) {
            setLoading(
              false
            );
          }
        }
      },

      [
        category,
        categoryKey,
        chart,
      ]
    );

  /* =======================================================
     LOAD
  ======================================================= */

  useEffect(() => {
    const controller =
      new AbortController();

    fetchSizeCharts(
      controller.signal
    );

    return () => {
      controller.abort();
    };
  }, [
    fetchSizeCharts,
    retryCount,
  ]);

  /* =======================================================
     SCROLL TOP
  ======================================================= */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [categoryKey]);

  /* =======================================================
     RETRY
  ======================================================= */

  const handleRetry =
    useCallback(() => {
      setRetryCount(
        (current) =>
          current + 1
      );
    }, []);

  /* =======================================================
     IMAGE ERROR
  ======================================================= */

  const handleImageError =
    useCallback(
      (failedImage) => {
        console.error(
          "FAILED AZURE SIZE CHART IMAGE:",
          failedImage
        );

        setImageSources(
          (
            currentImages
          ) =>
            currentImages.filter(
              (item) =>
                item.image !==
                failedImage
            )
        );
      },
      []
    );

  /* =======================================================
     INVALID CATEGORY
  ======================================================= */

  if (!chart) {
    return (
      <>
        <Navbar />

        <main className="flex min-h-screen items-center justify-center bg-[#fafafa] px-5 pt-[120px]">
          <div className="w-full max-w-[600px] rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center shadow-sm md:px-12">

            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#eef4ff] font-poppins text-2xl font-semibold text-[#1f4c94]">
              !
            </div>

            <h1 className="font-lora text-3xl text-gray-900 md:text-4xl">
              Size Chart Not
              Found
            </h1>

            <p className="mx-auto mt-4 max-w-[450px] font-poppins text-sm leading-6 text-gray-500">
              The requested Azure
              size chart could not
              be found.
            </p>

            <Link
              to="/"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[#1f4c94] px-8 py-3 font-poppins text-sm font-medium text-white transition hover:bg-[#173c77]"
            >
              Back to Home
            </Link>

          </div>
        </main>
      </>
    );
  }

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-[#fafafa] pb-24 pt-[120px] md:pt-[135px]">

        <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8">

          {/* =============================================
              BREADCRUMB
          ============================================= */}

          <div className="mb-7 flex flex-wrap items-center gap-2 font-poppins text-[13px]">

            <Link
              to="/"
              className="text-gray-400 transition hover:text-[#1f4c94]"
            >
              Home
            </Link>

            <span className="text-gray-300">
              /
            </span>

            <span className="font-medium text-gray-700">
              {chart.title}
            </span>

          </div>

          {/* =============================================
              HERO
          ============================================= */}

          <section className="relative mb-10 overflow-hidden rounded-[26px] border border-[#dce6f5] bg-white px-5 py-11 text-center shadow-[0_10px_40px_rgba(0,0,0,0.04)] sm:px-8 md:py-14">

            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#eef4ff] blur-3xl" />

            <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-[#f1f6ff] blur-3xl" />

            <div className="relative z-10 mx-auto max-w-[700px]">

              {/* BRAND */}

              <div className="mb-4 inline-flex items-center rounded-full border border-[#cbdaf0] bg-[#f3f7fd] px-4 py-2 font-poppins text-[10px] font-semibold uppercase tracking-[2.5px] text-[#1f4c94]">
                Azure Size Guide
              </div>

              {/* TITLE */}

              <h1 className="font-lora text-[34px] leading-tight text-gray-900 sm:text-[42px] md:text-[50px]">
                {chart.title}
              </h1>

              {/* DESCRIPTION */}

              <p className="mx-auto mt-4 max-w-[620px] font-poppins text-[14px] leading-7 text-gray-500 sm:text-[15px]">
                {
                  chart.description
                }
              </p>

              {!loading &&
                !error &&
                imageSources.length >
                  0 && (
                  <p className="mt-4 font-poppins text-xs text-gray-400">
                    Showing newest
                    size chart first
                  </p>
                )}

            </div>
          </section>

          {/* =============================================
              CONTENT
          ============================================= */}

          <section
            aria-live="polite"
            className="mx-auto w-full"
          >

            {/* LOADING */}

            {loading && (
              <div className="flex min-h-[400px] flex-col items-center justify-center rounded-[24px] border border-gray-200 bg-white px-5 text-center shadow-sm">

                <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-[#dce7f7] border-t-[#1f4c94]" />

                <p className="mt-5 font-poppins text-sm font-medium text-gray-600">
                  Loading{" "}
                  {chart.title}
                </p>

                <p className="mt-2 font-poppins text-xs text-gray-400">
                  Please wait a
                  moment
                </p>

              </div>
            )}

            {/* ERROR */}

            {!loading &&
              error && (
                <div
                  role="alert"
                  className="flex min-h-[400px] flex-col items-center justify-center rounded-[24px] border border-gray-200 bg-white px-6 text-center shadow-sm"
                >

                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eef4ff] font-poppins text-xl font-semibold text-[#1f4c94]">
                    !
                  </div>

                  <h2 className="mt-5 font-lora text-2xl text-gray-900">
                    Size Chart
                    Unavailable
                  </h2>

                  <p className="mt-3 max-w-[550px] font-poppins text-sm leading-6 text-gray-500">
                    {error}
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleRetry
                    }
                    className="mt-7 rounded-full bg-[#1f4c94] px-8 py-3 font-poppins text-sm font-medium text-white shadow-sm transition hover:bg-[#173c77]"
                  >
                    Try Again
                  </button>

                </div>
              )}

            {/* =============================================
                GRID
            ============================================= */}

            {!loading &&
              !error &&
              imageSources.length >
                0 && (

                <div
                  className={
                    getGridClass(
                      imageSources.length
                    )
                  }
                >

                  {imageSources.map(
                    (
                      item,
                      index
                    ) => {
                      const isLatest =
                        index === 0;

                      return (
                        <article
                          key={
                            item.id
                          }
                          className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.045)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(0,0,0,0.08)]"
                        >

                          {/* CARD HEADER */}

                          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">

                            <div className="min-w-0">

                              <div className="flex flex-wrap items-center gap-2">

                                <span className="font-poppins text-[10px] font-semibold uppercase tracking-[1.8px] text-[#1f4c94]">
                                  {
                                    item.category
                                  }
                                </span>

                                {isLatest && (
                                  <span className="rounded-full bg-[#eef4ff] px-2.5 py-1 font-poppins text-[9px] font-semibold uppercase tracking-[1px] text-[#1f4c94]">
                                    Latest
                                  </span>
                                )}

                              </div>

                              <h2 className="mt-1.5 font-lora text-xl leading-tight text-gray-900">
                                {
                                  chart.shortTitle
                                }{" "}
                                Size Chart
                              </h2>

                              {/* BRAND */}

                              <p className="mt-1 font-poppins text-[10px] font-medium uppercase tracking-[1px] text-gray-400">
                                Azure
                              </p>

                            </div>

                            {/* YEAR */}

                            {item.year && (
                              <div className="shrink-0 rounded-xl border border-[#cbdaf0] bg-[#f3f7fd] px-3 py-2 font-lora text-[17px] font-semibold text-[#1f4c94]">
                                {
                                  item.year
                                }
                              </div>
                            )}

                          </div>

                          {/* IMAGE */}

                          <div className="flex flex-1 items-center justify-center bg-white p-3 sm:p-4">

                            <div className="flex w-full items-center justify-center overflow-hidden rounded-xl bg-[#fdfdfd]">

                              <img
                                src={
                                  item.image
                                }
                                alt={`Azure ${chart.shortTitle} Size Chart${
                                  item.year
                                    ? ` ${item.year}`
                                    : ""
                                }`}
                                className="block h-auto max-h-[680px] w-full object-contain transition duration-300 group-hover:scale-[1.01]"
                                loading={
                                  index === 0
                                    ? "eager"
                                    : "lazy"
                                }
                                onError={() =>
                                  handleImageError(
                                    item.image
                                  )
                                }
                              />

                            </div>
                          </div>

                          {/* FOOTER */}

                          <div className="border-t border-gray-100 bg-[#fcfcfc] px-5 py-4">

                            <p className="mb-3 font-poppins text-[11px] leading-5 text-gray-400">
                              Official{" "}
                              {
                                item.brand
                              }{" "}
                              size chart
                              {item.year
                                ? ` for ${item.year}`
                                : ""}
                              .
                            </p>

                            <a
                              href={
                                item.image
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex w-full items-center justify-center rounded-full bg-[#1f4c94] px-5 py-2.5 font-poppins text-[11px] font-medium text-white transition hover:bg-[#173c77]"
                            >
                              View Full
                              Size
                            </a>

                          </div>
                        </article>
                      );
                    }
                  )}

                </div>
              )}

            {/* =============================================
                EMPTY
            ============================================= */}

            {!loading &&
              !error &&
              imageSources.length ===
                0 && (

                <div className="flex min-h-[380px] items-center justify-center rounded-[24px] border border-gray-200 bg-white px-5 text-center shadow-sm">

                  <div>

                    <h2 className="font-lora text-2xl text-gray-800">
                      No Size
                      Charts Found
                    </h2>

                    <p className="mt-3 font-poppins text-sm text-gray-500">
                      No Azure size
                      chart images are
                      currently
                      available.
                    </p>

                  </div>

                </div>
              )}

          </section>

          {/* =============================================
              BACK
          ============================================= */}

          <div className="mt-12 flex justify-center">

            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full border border-[#1f4c94] bg-white px-8 py-3 font-poppins text-sm font-medium text-[#1f4c94] transition hover:bg-[#1f4c94] hover:text-white"
            >
              ← Back to
              Collection
            </Link>

          </div>

        </div>
      </main>
    </>
  );
}